import { Route, Routes } from 'react-router';
import { KycWaitingResultsRoute } from '#pages/waiting-results/route';
import { kycRoutes } from '#routes';
import { KycPage } from '#page/page';
import { KycAccessGuard } from '#guard/guard';
import { KycLayout } from '#layout/layout';
import { KycContextProvider, type KycProviderProps } from './context/kyc-context';
import {
  type KycProviderDetailsContextValue,
  KycProviderDetailsProvider,
} from './context/provider-details-context';
import { KycSuspenseBoundary } from './kyc-suspense-boundary';

export type KycRouterProps = KycProviderProps & KycProviderDetailsContextValue;

/**
 * Self-contained KYC verification flow.
 * Wraps context, error boundaries, suspense, and routing into a single component.
 *
 * Internally renders `KycLayout → KycAccessGuard → KycPage`, which selects the appropriate provider
 * (Fourthline or IDnow) based on the user's KYC status. If KYC is already completed,
 * the guard calls `onComplete` immediately.
 *
 * @see {@link KycContextValue} for prop documentation.
 *
 * @example
 * ```tsx
 * <KycRouter
 *   provider={KycProvider.fourthline}
 *   providerDetails={providerDetails}
 *   legalCountry={company.legalCountry}
 *   hasGermanResidencePermit={false}
 *   onComplete={handleComplete}
 *   trackingPrefix="verification"
 *   membershipId={membership.id}   // optional, for tracking
 *   userId={membership.userId}      // optional, for tracking
 * />
 * ```
 */
export function KycRouter({
  provider,
  providerDetails,
  refetchProviderDetails,
  ...contextProps
}: KycRouterProps) {
  return (
    <KycContextProvider {...contextProps}>
      <KycProviderDetailsProvider
        provider={provider}
        providerDetails={providerDetails}
        refetchProviderDetails={refetchProviderDetails}
      >
        <KycSuspenseBoundary>
          <Routes>
            <Route element={<KycLayout />}>
              <Route element={<KycAccessGuard />}>
                <Route element={<KycPage />} index />
              </Route>
              <Route
                element={<KycWaitingResultsRoute />}
                path={kycRoutes.waitingResultsSlug.basePath}
              />
            </Route>
          </Routes>
        </KycSuspenseBoundary>
      </KycProviderDetailsProvider>
    </KycContextProvider>
  );
}
